import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    FormControlLabel,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    MenuItem,
    Snackbar,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
    Switch,
} from '@mui/material';
import PersonAddAltOutlinedIcon from '@mui/icons-material/PersonAddAltOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import PowerSettingsNewOutlinedIcon from '@mui/icons-material/PowerSettingsNewOutlined';
import AdminPanelSettingsOutlinedIcon from '@mui/icons-material/AdminPanelSettingsOutlined';
import MarkEmailReadOutlinedIcon from '@mui/icons-material/MarkEmailReadOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ExpandMoreOutlinedIcon from '@mui/icons-material/ExpandMoreOutlined';
import RefreshOutlinedIcon from '@mui/icons-material/RefreshOutlined';
import { uppercaseLabel } from '../../styles/typography';
import { callBackend } from '../../utils/api';
import TeamMemberOffboardingDialog from './TeamMemberOffboardingDialog';
import {
    secondaryActionButtonSx,
    secondarySectionFrameSx,
} from '../../styles/secondaryMsiStyles';

const EMPTY_FORM = {
    name: '',
    email: '',
    organization_id: '',
    counts_toward_seat_limit: true,
};
const UNPROFILED_DISCLOSURE_ROW = Symbol('unprofiled-firebase-identities-disclosure');

const IDENTITY_STATUS = {
    fully_active: {
        label: 'Fully active',
        description: 'The database and Firebase login are linked and active.',
        healthy: true,
    },
    linked_disabled: {
        label: 'Fully disabled',
        description: 'The database and Firebase login are both intentionally disabled.',
        healthy: true,
    },
    postgres_only: {
        label: 'Database only',
        description: 'The database account exists, but its Firebase Authentication record is missing.',
    },
    firebase_only: {
        label: 'Firebase only',
        description: 'The Firebase Authentication record exists, but its application database account is missing.',
    },
    invite_pending: {
        label: 'Invite pending',
        description: 'The linked account is waiting for its first sign-in method or password setup. Its invite can be resent.',
    },
    firebase_login_unusable: {
        label: 'Login unavailable',
        description: 'Firebase has this UID, but it does not have a supported sign-in method required to sign in.',
    },
    email_mismatch: {
        label: 'Email mismatch',
        description: 'The database and Firebase records share a UID but have different emails. Review is required before deletion.',
    },
    activation_mismatch: {
        label: 'Status mismatch',
        description: 'The database active state and Firebase disabled state do not agree.',
    },
    identity_unknown: {
        label: 'Identity unknown',
        description: 'The server returned an identity state this client cannot safely interpret.',
    },
};

function identityView(user) {
    const unknown = {
        postgresPresent: null,
        firebasePresent: null,
        firebaseLoginUsable: null,
        firebaseInviteEligible: null,
        firebaseDisabled: null,
        status: 'identity_unknown',
        statusConfig: IDENTITY_STATUS.identity_unknown,
        contractValid: false,
    };
    if (!user || typeof user !== 'object') {
        return unknown;
    }

    const uid = typeof user.firebase_uid === 'string' ? user.firebase_uid.trim() : '';
    const postgresPresent = user.postgres_present;
    const firebasePresent = user.firebase_present;
    const firebaseLoginUsable = user.firebase_login_usable;
    const firebaseInviteEligible = user.firebase_invite_eligible;
    const firebaseDisabled = user.firebase_disabled;
    const status = user.identity_status;
    const requiredTypesValid = Boolean(uid) && uid === user.firebase_uid
        && typeof postgresPresent === 'boolean'
        && typeof firebasePresent === 'boolean'
        && typeof firebaseLoginUsable === 'boolean'
        && typeof firebaseInviteEligible === 'boolean'
        && (typeof firebaseDisabled === 'boolean' || firebaseDisabled === null)
        && typeof user.firebase_missing === 'boolean'
        && typeof user.can_admin === 'boolean'
        && typeof user.name === 'string'
        && (typeof user.email === 'string' || user.email === null)
        && typeof status === 'string'
        && status !== 'identity_unknown'
        && Boolean(IDENTITY_STATUS[status]);
    if (!requiredTypesValid || user.firebase_missing !== !firebasePresent) {
        return unknown;
    }

    const postgresActive = typeof user.is_active === 'boolean'
        ? user.is_active
        : (user.is_active === 0 || user.is_active === 1 ? Boolean(user.is_active) : null);
    const postgresOnlyWithoutActiveState = postgresPresent && !firebasePresent
        && status === 'postgres_only' && user.is_active === null;
    if ((postgresPresent && postgresActive === null && !postgresOnlyWithoutActiveState)
        || (!postgresPresent && user.is_active !== null)) {
        return unknown;
    }
    if (!firebasePresent && (
        firebaseLoginUsable
        || firebaseInviteEligible
        || firebaseDisabled !== null
        || user.can_admin
    )) {
        return unknown;
    }

    const hasEmail = typeof user.email === 'string' && Boolean(user.email.trim());
    const statusCompatible = {
        postgres_only: postgresPresent && !firebasePresent && !firebaseLoginUsable
            && !firebaseInviteEligible,
        firebase_only: !postgresPresent && firebasePresent
            && !firebaseInviteEligible,
        invite_pending: postgresPresent && firebasePresent && !firebaseLoginUsable
            && firebaseInviteEligible && hasEmail,
        firebase_login_unusable: postgresPresent && firebasePresent && !firebaseLoginUsable,
        email_mismatch: postgresPresent && firebasePresent && hasEmail
            && !firebaseInviteEligible,
        activation_mismatch: postgresPresent && firebasePresent && firebaseLoginUsable
            && !firebaseInviteEligible && hasEmail
            && (firebaseDisabled === null || postgresActive === firebaseDisabled),
        fully_active: postgresPresent && firebasePresent && firebaseLoginUsable
            && !firebaseInviteEligible && hasEmail
            && postgresActive === true && firebaseDisabled === false,
        linked_disabled: postgresPresent && firebasePresent && firebaseLoginUsable
            && !firebaseInviteEligible && hasEmail
            && postgresActive === false && firebaseDisabled === true,
    }[status];
    if (!statusCompatible) {
        return unknown;
    }

    return {
        postgresPresent,
        firebasePresent,
        firebaseLoginUsable,
        firebaseInviteEligible,
        firebaseDisabled,
        status,
        statusConfig: IDENTITY_STATUS[status],
        contractValid: true,
    };
}

function actionTooltip(available, normalCopy, unavailableCopy) {
    return available ? normalCopy : unavailableCopy;
}

// FastAPI HTTPException bodies come back as `{"detail": "..."}"`; callBackend
// only exposes the raw "status - body" text, so pull the human-readable
// message out of it (e.g. the 409 last-admin-lockout message) instead of
// showing the whole "Cloud Run call failed: 409 - {...}" string.
function extractApiErrorMessage(error, fallback) {
    const raw = error?.message || '';
    const marker = ' - ';
    const idx = raw.indexOf(marker);
    const bodyText = idx >= 0 ? raw.slice(idx + marker.length) : raw;
    try {
        const parsed = JSON.parse(bodyText);
        if (parsed?.detail) {
            if (typeof parsed.detail === 'string') return parsed.detail;
            // Coded errors arrive as {"message": "...", "code": "..."}.
            if (typeof parsed.detail.message === 'string' && parsed.detail.message.trim()) {
                return parsed.detail.message;
            }
            return JSON.stringify(parsed.detail);
        }
    } catch {
        // Not JSON -- fall through to the raw text.
    }
    return bodyText || raw || fallback;
}

export default function AdminUsersPanel({ currentUid, currentEnv, users, invitations, organizations, loading, error, onDismissError, onRefresh }) {
    const [actionError, setActionError] = useState('');
    const [busyUid, setBusyUid] = useState(null);

    const [formOpen, setFormOpen] = useState(false);
    const [editingUser, setEditingUser] = useState(null); // null => create mode
    const [form, setForm] = useState(EMPTY_FORM);
    const [formError, setFormError] = useState('');
    const [formSubmitting, setFormSubmitting] = useState(false);

    const [infoMessage, setInfoMessage] = useState('');
    const [warningMessage, setWarningMessage] = useState('');

    const [deleteTarget, setDeleteTarget] = useState(null); // user | null
    const [unprofiledExpanded, setUnprofiledExpanded] = useState(false);

    const openCreateDialog = () => {
        setEditingUser(null);
        const defaultOrganization = organizations.find((item) => item?.status === 'active');
        setForm({
            ...EMPTY_FORM,
            organization_id: defaultOrganization?.id || '',
        });
        setFormError('');
        setFormOpen(true);
    };

    const openEditDialog = (user) => {
        const defaultOrganization = organizations.find((item) => item?.status === 'active');
        setEditingUser(user);
        setForm({
            ...EMPTY_FORM,
            name: user.name || '',
            email: user.email || '',
            organization_id: user.organization_id || defaultOrganization?.id || '',
            counts_toward_seat_limit: user.counts_toward_seat_limit !== false,
        });
        setFormError('');
        setFormOpen(true);
    };

    const closeDialog = () => {
        if (formSubmitting) return;
        setFormOpen(false);
    };

    const submitForm = async () => {
        setFormSubmitting(true);
        setFormError('');
        try {
            if (editingUser) {
                await callBackend(`admin/users/${editingUser.firebase_uid}`, 'PATCH', {
                    name: form.name.trim(),
                    organization_id: form.organization_id,
                    counts_toward_seat_limit: form.counts_toward_seat_limit,
                });
            } else {
                const resp = await callBackend('admin/users', 'POST', {
                    name: form.name.trim(),
                    email: form.email.trim(),
                    organization_id: form.organization_id,
                    counts_toward_seat_limit: form.counts_toward_seat_limit,
                });
                if (resp?.invite_sent) {
                    setWarningMessage('');
                    setInfoMessage(`Invitation sent to ${form.email.trim()}.`);
                } else {
                    setInfoMessage('');
                    setWarningMessage(
                        `A pending invitation was created for ${form.email.trim()}, but the email was not sent. `
                        + 'Use Resend invitation to try again.'
                    );
                }
            }
            setFormOpen(false);
            await onRefresh();
        } catch (err) {
            setFormError(extractApiErrorMessage(err, 'Failed to save user.'));
        } finally {
            setFormSubmitting(false);
        }
    };

    const runUserAction = async (uid, action) => {
        setBusyUid(uid);
        setActionError('');
        try {
            await action();
            await onRefresh();
        } catch (err) {
            setActionError(extractApiErrorMessage(err, 'Action failed.'));
        } finally {
            setBusyUid(null);
        }
    };

    const toggleActive = (user) => runUserAction(user.firebase_uid, () => (
        user.is_active
            ? callBackend(`admin/users/${user.firebase_uid}/disable`, 'POST')
            : callBackend(`admin/users/${user.firebase_uid}`, 'PATCH', { active: true })
    ));

    const toggleAdmin = (user) => runUserAction(user.firebase_uid, async () => {
        const nextCanAdmin = !user.can_admin;
        await callBackend(`admin/users/${user.firebase_uid}`, 'PATCH', { can_admin: nextCanAdmin });
        // Custom claims are baked into the ID token at mint time -- this change
        // won't be visible to the affected user until their next token refresh
        // (~1hr) or their next login, same staleness the CLI's grant/revoke warns about.
        setInfoMessage(
            `${nextCanAdmin ? 'Granted' : 'Revoked'} admin access for ${user.email} in ${currentEnv}. `
            + `They won't see this change until they log out and back in (or ~1hr).`
        );
    });

    const resendInvite = (user) => runUserAction(user.firebase_uid, async () => {
        const resp = await callBackend(`admin/users/${user.firebase_uid}/resend-invite`, 'POST');
        if (resp?.invite_sent) {
            setWarningMessage('');
            setInfoMessage(`Invite email sent to ${user.email}.`);
        } else {
            setInfoMessage('');
            setWarningMessage(
                `Invite email was not sent to ${user.email}. Please try Resend invite again.`
            );
        }
    });

    const resendPendingInvitation = (invitation) => runUserAction(
        `invitation:${invitation.invitation_id}`,
        async () => {
            const resp = await callBackend(
                `admin/users/invitations/${invitation.invitation_id}/resend`,
                'POST',
            );
            if (resp?.invite_sent) {
                setWarningMessage('');
                setInfoMessage(`Invitation sent to ${invitation.email}.`);
            } else {
                setInfoMessage('');
                setWarningMessage(
                    `Invitation email was not sent to ${invitation.email}. Please try again.`
                );
            }
        },
    );

    const revokePendingInvitation = (invitation) => runUserAction(
        `invitation:${invitation.invitation_id}`,
        async () => {
            await callBackend(
                `admin/users/invitations/${invitation.invitation_id}`,
                'DELETE',
            );
            setWarningMessage('');
            setInfoMessage(`Invitation revoked for ${invitation.email}.`);
        },
    );

    const closeDeleteDialog = () => {
        setDeleteTarget(null);
    };

    const isUnprofiledFirebaseIdentity = (candidate) => (
        candidate?.postgres_present === false
        && candidate?.firebase_present === true
        && !(typeof candidate?.email === 'string' && candidate.email.trim())
    );
    const teamMembers = users.filter((candidate) => !isUnprofiledFirebaseIdentity(candidate));
    const unprofiledFirebaseIdentities = users.filter(isUnprofiledFirebaseIdentity);
    const visibleInventoryRows = unprofiledFirebaseIdentities.length > 0
        ? [
            ...teamMembers,
            UNPROFILED_DISCLOSURE_ROW,
            ...(unprofiledExpanded ? unprofiledFirebaseIdentities : []),
        ]
        : teamMembers;
    const invitationRows = Array.isArray(invitations) ? invitations : [];
    const validInvitations = invitationRows.filter((invitation) => {
        const hasCustomerContext = (
            typeof invitation?.organization_id === 'string'
            && Boolean(invitation.organization_id.trim())
            && typeof invitation.organization_name === 'string'
            && typeof invitation.counts_toward_seat_limit === 'boolean'
        );
        const requiresReissue = (
            invitation?.requires_reissue === true
            && invitation?.organization_id === null
        );
        return invitation
        && typeof invitation.invitation_id === 'string'
        && invitation.invitation_id.trim() === invitation.invitation_id
        && typeof invitation.email === 'string'
        && Boolean(invitation.email.trim())
        && typeof invitation.name === 'string'
        && Boolean(invitation.name.trim())
        && ['pending', 'expired'].includes(invitation.status)
        && (hasCustomerContext || requiresReissue);
    });
    const reissueInvitationCount = validInvitations.filter(
        (invitation) => invitation.requires_reissue === true,
    ).length;
    const invalidInvitationCount = Array.isArray(invitations)
        ? invitationRows.length - validInvitations.length
        : 1;

    return (
        <Box sx={{ display: 'grid', gap: 1.5 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
                <Typography sx={{ fontSize: '0.82rem', color: 'text.secondary' }}>
                    Admin access shown below applies to <strong>{currentEnv}</strong> only -- grants/revokes here
                    never affect other environments.
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <Tooltip title="Refresh users">
                        <span>
                            <IconButton disabled={loading} onClick={() => onRefresh()}>
                                <RefreshOutlinedIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                        </span>
                    </Tooltip>
                    <Button
                        variant="outlined"
                        startIcon={<PersonAddAltOutlinedIcon fontSize="small" />}
                        onClick={openCreateDialog}
                        sx={{ minHeight: 34, px: 1.6, whiteSpace: 'nowrap', ...uppercaseLabel({ fontSize: '0.74rem', letterSpacing: '0.04em' }), ...secondaryActionButtonSx }}
                    >
                        Invite user
                    </Button>
                </Box>
            </Box>

            {error && <Alert severity="error" onClose={onDismissError}>{error}</Alert>}
            {actionError && <Alert severity="error" onClose={() => setActionError('')}>{actionError}</Alert>}
            {reissueInvitationCount > 0 && (
                <Alert severity="warning">
                    {reissueInvitationCount} invitation{reissueInvitationCount === 1 ? '' : 's'} {reissueInvitationCount === 1 ? 'predates' : 'predate'} customer access management. Revoke and reissue with an organization before deployment.
                </Alert>
            )}
            {invalidInvitationCount > 0 && (
                <Alert severity="warning">
                    One or more pending invitations could not be validated and are hidden. Refresh or contact support before taking action.
                </Alert>
            )}

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                    <CircularProgress size={22} />
                </Box>
            ) : (
                <>
                    <Box
                        aria-label="Identity inventory counts"
                        sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}
                    >
                        <Chip label={`Team Members: ${teamMembers.length}`} size="small" />
                        <Chip label={`Pending invitations: ${validInvitations.length}`} size="small" variant="outlined" />
                        <Chip
                            label={`Unprofiled Firebase identities: ${unprofiledFirebaseIdentities.length}`}
                            size="small"
                            variant="outlined"
                        />
                    </Box>
                    {validInvitations.length > 0 && (
                        <TableContainer sx={{ ...secondarySectionFrameSx, overflowX: 'auto' }}>
                            <Table size="small" aria-label="Pending invitations">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Name</TableCell>
                                        <TableCell>Email</TableCell>
                                        <TableCell>Organization</TableCell>
                                        <TableCell>Status</TableCell>
                                        <TableCell align="right">Actions</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {validInvitations.map((invitation) => {
                                        const invitationBusy = busyUid === `invitation:${invitation.invitation_id}`;
                                        const requiresReissue = invitation.requires_reissue === true;
                                        return (
                                            <TableRow key={invitation.invitation_id}>
                                                <TableCell>{invitation.name}</TableCell>
                                                <TableCell>{invitation.email}</TableCell>
                                                <TableCell>
                                                    {requiresReissue ? 'Reissue required' : invitation.organization_name}
                                                    {!requiresReissue && !invitation.counts_toward_seat_limit && (
                                                        <Chip label="Complimentary" size="small" variant="outlined" sx={{ ml: 0.75 }} />
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    <Chip
                                                        label={requiresReissue
                                                            ? 'Revoke and reissue'
                                                            : (invitation.status === 'expired' ? 'Expired' : 'Awaiting acceptance')}
                                                        color={invitation.status === 'expired' || requiresReissue ? 'warning' : 'info'}
                                                        size="small"
                                                        variant="outlined"
                                                    />
                                                </TableCell>
                                                <TableCell align="right">
                                                    <Tooltip title={requiresReissue
                                                        ? 'Revoke this invitation and issue a new one with an organization'
                                                        : 'Rotate the secure link and resend this invitation'}>
                                                        <span>
                                                            <IconButton
                                                                disabled={invitationBusy || requiresReissue}
                                                                onClick={() => resendPendingInvitation(invitation)}
                                                                aria-label={`Resend invitation to ${invitation.email}`}
                                                            >
                                                                <MarkEmailReadOutlinedIcon sx={{ fontSize: 18 }} />
                                                            </IconButton>
                                                        </span>
                                                    </Tooltip>
                                                    <Tooltip title="Revoke this pending invitation">
                                                        <span>
                                                            <IconButton
                                                                disabled={invitationBusy}
                                                                color="error"
                                                                onClick={() => revokePendingInvitation(invitation)}
                                                                aria-label={`Revoke invitation for ${invitation.email}`}
                                                            >
                                                                <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                                                            </IconButton>
                                                        </span>
                                                    </Tooltip>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                    <TableContainer
                        data-testid="team-members-table-container"
                        sx={{
                            ...secondarySectionFrameSx,
                            maxHeight: 'none',
                            overflow: 'visible',
                            overflowX: 'auto',
                            overflowY: 'visible',
                        }}
                    >
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Name</TableCell>
                                <TableCell>Email</TableCell>
                                <TableCell>Organization</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>Admin ({currentEnv})</TableCell>
                                <TableCell align="right">Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {teamMembers.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={6} sx={{ textAlign: 'center', color: 'text.secondary', py: 4 }}>
                                        No team members found.
                                    </TableCell>
                                </TableRow>
                            )}
                            {visibleInventoryRows.map((user, userIndex) => {
                                if (user === UNPROFILED_DISCLOSURE_ROW) {
                                    return (
                                        <TableRow key="unprofiled-firebase-identities-disclosure">
                                            <TableCell colSpan={6} sx={{ py: 1.25 }}>
                                                <Box sx={{ display: 'grid', gap: 0.5 }}>
                                                    <Button
                                                        aria-expanded={unprofiledExpanded}
                                                        aria-controls="unprofiled-firebase-identities-rows"
                                                        onClick={() => setUnprofiledExpanded((expanded) => !expanded)}
                                                        startIcon={(
                                                            <ExpandMoreOutlinedIcon
                                                                sx={{
                                                                    transform: unprofiledExpanded
                                                                        ? 'rotate(180deg)'
                                                                        : 'rotate(0deg)',
                                                                    transition: 'transform 120ms ease',
                                                                }}
                                                            />
                                                        )}
                                                        sx={{ justifySelf: 'start', ...secondaryActionButtonSx }}
                                                    >
                                                        {unprofiledExpanded ? 'Hide' : 'Show'} Unprofiled Firebase identities
                                                        {' '}({unprofiledFirebaseIdentities.length})
                                                    </Button>
                                                    <Typography sx={{ color: 'text.secondary', fontSize: '0.76rem' }}>
                                                        Firebase identities without a Postgres profile or Firebase email.
                                                        Their origin is unknown; expand only for identity diagnostics.
                                                    </Typography>
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    );
                                }
                                const {
                                    postgresPresent,
                                    firebasePresent,
                                    firebaseLoginUsable,
                                    firebaseDisabled,
                                    firebaseInviteEligible,
                                    status,
                                    statusConfig,
                                    contractValid,
                                } = identityView(user);
                                const hasDatabaseActiveState = postgresPresent
                                    && (typeof user.is_active === 'boolean'
                                        || user.is_active === 0 || user.is_active === 1);
                                const isActive = Boolean(user.is_active);
                                const isBusy = busyUid === user.firebase_uid;
                                const isSelf = user.firebase_uid === currentUid;
                                const displayName = typeof user.name === 'string' && user.name.trim()
                                    ? user.name
                                    : 'Unknown member';
                                const displayEmail = typeof user.email === 'string' && user.email.trim()
                                    ? user.email
                                    : null;
                                const memberLabel = displayEmail || displayName;
                                const hasConfirmableEmail = Boolean(displayEmail);
                                const hasEmailMismatch = status === 'email_mismatch';
                                const invalidIdentityCopy = status === 'identity_unknown'
                                    ? 'Unavailable because the identity response could not be validated.'
                                    : null;
                                const canEdit = contractValid && postgresPresent && firebasePresent
                                    && !hasEmailMismatch;
                                const canResendInvite = contractValid && firebaseInviteEligible;
                                const canChangeAdmin = contractValid && firebasePresent
                                    && hasConfirmableEmail && !hasEmailMismatch;
                                const canChangeActive = contractValid && postgresPresent && firebasePresent
                                    && !hasEmailMismatch;
                                const canOffboard = contractValid && !isSelf
                                    && status !== 'email_mismatch'
                                    && Boolean(user.firebase_uid)
                                    && (postgresPresent || firebasePresent)
                                    && hasConfirmableEmail;
                                const normalStatusLabel = status === 'linked_disabled'
                                    ? 'Disabled'
                                    : status === 'fully_active'
                                        ? 'Active'
                                        : null;
                                const sourceStatusLabel = postgresPresent && hasDatabaseActiveState
                                    ? `Database ${isActive ? 'active' : 'disabled'}`
                                    : postgresPresent
                                        ? 'Database state unknown'
                                    : firebasePresent === true && firebaseDisabled === true
                                        ? 'Firebase disabled'
                                        : firebasePresent === true && firebaseDisabled === false
                                            ? 'Firebase active'
                                            : 'Source state unknown';
                                const postgresDiagnostic = postgresPresent === true
                                    ? 'present'
                                    : postgresPresent === false ? 'missing' : 'unknown';
                                const firebaseDiagnostic = firebasePresent === true
                                    ? 'present'
                                    : firebasePresent === false ? 'missing' : 'unknown';
                                const loginDiagnostic = firebaseLoginUsable === true
                                    ? 'usable'
                                    : firebaseLoginUsable === false ? 'unavailable' : 'unknown';
                                return (
                                    <TableRow
                                        aria-label={isUnprofiledFirebaseIdentity(user)
                                            ? 'Unprofiled Firebase identity row'
                                            : undefined}
                                        id={isUnprofiledFirebaseIdentity(user)
                                            && userIndex === teamMembers.length + 1
                                            ? 'unprofiled-firebase-identities-rows'
                                            : undefined}
                                        key={user.firebase_uid || `identity-unknown-${userIndex}`}
                                        hover
                                    >
                                        <TableCell>
                                            {displayName}
                                            {isSelf && (
                                                <Chip label="You" size="small" sx={{ ml: 0.75 }} />
                                            )}
                                        </TableCell>
                                        <TableCell>{displayEmail || 'Not available'}</TableCell>
                                        <TableCell>
                                            {user.organization_name || 'Not assigned'}
                                            {user.organization_id && user.counts_toward_seat_limit === false && (
                                                <Chip label="Complimentary" size="small" variant="outlined" sx={{ ml: 0.75 }} />
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.5 }}>
                                                <Chip
                                                    label={normalStatusLabel || sourceStatusLabel}
                                                    color={normalStatusLabel === 'Active' ? 'success' : 'default'}
                                                    size="small"
                                                />
                                                {!statusConfig.healthy && (
                                                    <Tooltip title={statusConfig.description}>
                                                        <Chip
                                                            label={statusConfig.label}
                                                            aria-label={`Identity status for ${memberLabel}: ${statusConfig.label}`}
                                                            color="warning"
                                                            variant="outlined"
                                                            size="small"
                                                        />
                                                    </Tooltip>
                                                )}
                                                <Chip
                                                    label={`Postgres ${postgresDiagnostic}`}
                                                    aria-label={`${memberLabel}: Postgres ${postgresDiagnostic}`}
                                                    color={postgresPresent === true ? 'success' : postgresPresent === false ? 'warning' : 'default'}
                                                    variant="outlined"
                                                    size="small"
                                                />
                                                <Chip
                                                    label={`Firebase ${firebaseDiagnostic}`}
                                                    aria-label={`${memberLabel}: Firebase ${firebaseDiagnostic}`}
                                                    color={firebasePresent === true ? 'success' : firebasePresent === false ? 'warning' : 'default'}
                                                    variant="outlined"
                                                    size="small"
                                                />
                                                <Chip
                                                    label={`Login ${loginDiagnostic}`}
                                                    aria-label={`${memberLabel}: Firebase login ${loginDiagnostic}`}
                                                    color={firebaseLoginUsable === true ? 'success' : firebaseLoginUsable === false ? 'warning' : 'default'}
                                                    variant="outlined"
                                                    size="small"
                                                />
                                                <Chip
                                                    label={firebasePresent === true
                                                        ? `Firebase ${firebaseDisabled === true ? 'disabled' : firebaseDisabled === false ? 'enabled' : 'state unknown'}`
                                                        : firebasePresent === false ? 'Firebase state unavailable' : 'Firebase state unknown'}
                                                    aria-label={`${memberLabel}: ${firebasePresent === true
                                                        ? `Firebase ${firebaseDisabled === true ? 'disabled' : firebaseDisabled === false ? 'enabled' : 'state unknown'}`
                                                        : firebasePresent === false ? 'Firebase state unavailable' : 'Firebase state unknown'}`}
                                                    variant="outlined"
                                                    size="small"
                                                />
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                label={contractValid ? (user.can_admin ? 'Admin' : '—') : 'Unknown'}
                                                color={contractValid && user.can_admin ? 'primary' : 'default'}
                                                variant={contractValid && user.can_admin ? 'filled' : 'outlined'}
                                                size="small"
                                            />
                                        </TableCell>
                                        <TableCell align="right">
                                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
                                                <Tooltip title={actionTooltip(
                                                    canEdit,
                                                    'Edit name',
                                                    invalidIdentityCopy || (hasEmailMismatch
                                                        ? 'Review the email mismatch before editing this account.'
                                                        : 'Unavailable because editing requires linked Postgres and Firebase records.'),
                                                )}>
                                                    <span>
                                                        <IconButton
                                                            disabled={isBusy || !canEdit}
                                                            onClick={() => openEditDialog(user)}
                                                            aria-label={`Edit ${memberLabel}`}
                                                        >
                                                            <EditOutlinedIcon sx={{ fontSize: 18 }} />
                                                        </IconButton>
                                                    </span>
                                                </Tooltip>
                                                <Tooltip title={actionTooltip(
                                                    canResendInvite,
                                                    status === 'invite_pending'
                                                        ? 'Resend invite email to finish initial sign-in or password setup.'
                                                        : 'Resend invite email',
                                                    invalidIdentityCopy || (firebasePresent
                                                        ? hasEmailMismatch
                                                            ? 'Review the email mismatch before resending an invite.'
                                                            : !postgresPresent
                                                                ? 'Create a pending invitation to provision application membership. A password reset alone cannot grant access.'
                                                                : firebaseLoginUsable
                                                                ? 'This account already has a sign-in method. Password changes are self-service.'
                                                                : 'Unavailable because Firebase has no email address.'
                                                        : 'Unavailable because no Firebase account exists.'),
                                                )}>
                                                    <span>
                                                        <IconButton
                                                            disabled={isBusy || !canResendInvite}
                                                            onClick={() => resendInvite(user)}
                                                            aria-label={`Resend invite to ${memberLabel}`}
                                                        >
                                                            <MarkEmailReadOutlinedIcon sx={{ fontSize: 18 }} />
                                                        </IconButton>
                                                    </span>
                                                </Tooltip>
                                                <Tooltip title={actionTooltip(
                                                    canChangeAdmin,
                                                    user.can_admin ? `Revoke admin for ${currentEnv}` : `Grant admin for ${currentEnv}`,
                                                    invalidIdentityCopy || (hasEmailMismatch
                                                        ? 'Review the email mismatch before changing admin access.'
                                                        : firebasePresent && !hasConfirmableEmail
                                                            ? 'Unavailable because Firebase has no email address.'
                                                            : 'Unavailable because no Firebase account exists.'),
                                                )}>
                                                    <span>
                                                        <IconButton
                                                            disabled={isBusy || !canChangeAdmin}
                                                            color={user.can_admin ? 'primary' : 'default'}
                                                            onClick={() => toggleAdmin(user)}
                                                            aria-label={`${user.can_admin ? 'Revoke' : 'Grant'} admin for ${memberLabel}`}
                                                        >
                                                            <AdminPanelSettingsOutlinedIcon sx={{ fontSize: 18 }} />
                                                        </IconButton>
                                                    </span>
                                                </Tooltip>
                                                <Tooltip title={actionTooltip(
                                                    canChangeActive,
                                                    isActive ? 'Disable user' : 'Enable user',
                                                    invalidIdentityCopy || (hasEmailMismatch
                                                        ? 'Review the email mismatch before changing active status.'
                                                        : 'Unavailable because changing active status requires both Postgres and Firebase records.'),
                                                )}>
                                                    <span>
                                                        <IconButton
                                                            disabled={isBusy || !canChangeActive}
                                                            color={isActive ? 'error' : 'success'}
                                                            onClick={() => toggleActive(user)}
                                                            aria-label={`${isActive ? 'Disable' : 'Enable'} ${memberLabel}`}
                                                        >
                                                            <PowerSettingsNewOutlinedIcon sx={{ fontSize: 18 }} />
                                                        </IconButton>
                                                    </span>
                                                </Tooltip>
                                                <Tooltip title={actionTooltip(
                                                    canOffboard,
                                                    'Delete user permanently',
                                                    isSelf
                                                        ? 'You cannot permanently delete your own signed-in account here.'
                                                        : status === 'email_mismatch'
                                                            ? 'Review the email mismatch before permanent deletion.'
                                                            : status === 'identity_unknown'
                                                                ? 'Permanent deletion is unavailable because the identity response could not be validated.'
                                                                : firebasePresent && !postgresPresent && !hasConfirmableEmail
                                                                    ? 'Permanent deletion is unavailable because this Firebase-only record has no server-confirmed email for exact confirmation.'
                                                                    : !hasConfirmableEmail
                                                                        ? 'Permanent deletion is unavailable because no server-confirmed email is available for exact confirmation.'
                                                            : 'Permanent deletion is unavailable for this row.',
                                                )}>
                                                    <span>
                                                        <IconButton
                                                            disabled={isBusy || !canOffboard}
                                                            color="error"
                                                            onClick={() => setDeleteTarget(user)}
                                                            aria-label={`Delete ${memberLabel} permanently`}
                                                        >
                                                            <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                                                        </IconButton>
                                                    </span>
                                                </Tooltip>
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                    </TableContainer>
                </>
            )}

            <Dialog open={formOpen} onClose={closeDialog} maxWidth="xs" fullWidth>
                <DialogTitle sx={{ fontSize: '0.95rem' }}>
                    {editingUser ? 'Edit user' : 'Invite user'}
                </DialogTitle>
                <DialogContent sx={{ display: 'grid', gap: 1.75, pt: 1 }}>
                    {formError && <Alert severity="error">{formError}</Alert>}
                    <TextField
                        label="Name"
                        size="small"
                        value={form.name}
                        onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
                        fullWidth
                        autoFocus
                    />
                    <TextField
                        label="Email"
                        size="small"
                        type="email"
                        value={form.email}
                        onChange={(e) => setForm((prev) => ({ ...prev, email: e.target.value }))}
                        fullWidth
                        disabled={Boolean(editingUser)}
                        helperText={editingUser ? 'Email cannot be changed here.' : undefined}
                    />
                    <TextField
                        select
                        label="Customer organization"
                        size="small"
                        value={form.organization_id}
                        onChange={(e) => setForm((prev) => ({ ...prev, organization_id: e.target.value }))}
                        fullWidth
                        helperText={organizations.length === 0 ? 'Create an active customer organization before assigning users.' : undefined}
                    >
                        {organizations.filter((organization) => organization?.status === 'active').map((organization) => (
                            <MenuItem key={organization.id} value={organization.id}>{organization.display_name}</MenuItem>
                        ))}
                    </TextField>
                    <FormControlLabel
                        control={(
                            <Switch
                                checked={!form.counts_toward_seat_limit}
                                onChange={(e) => setForm((prev) => ({ ...prev, counts_toward_seat_limit: !e.target.checked }))}
                            />
                        )}
                        label="Complimentary user (does not consume a seat)"
                    />
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={closeDialog} disabled={formSubmitting}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={submitForm}
                        disabled={formSubmitting || !form.name.trim() || !form.organization_id || (!editingUser && !form.email.trim())}
                    >
                        {formSubmitting ? <CircularProgress size={18} /> : editingUser ? 'Save' : 'Send invitation'}
                    </Button>
                </DialogActions>
            </Dialog>

            <TeamMemberOffboardingDialog
                target={deleteTarget}
                onClose={closeDeleteDialog}
                onCompleted={onRefresh}
            />

            <Snackbar
                open={Boolean(warningMessage)}
                onClose={() => setWarningMessage('')}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                autoHideDuration={10000}
            >
                <Alert
                    severity="warning"
                    onClose={() => setWarningMessage('')}
                    sx={{ maxWidth: 420 }}
                >
                    {warningMessage}
                </Alert>
            </Snackbar>

            <Snackbar
                open={Boolean(infoMessage)}
                onClose={() => setInfoMessage('')}
                autoHideDuration={6000}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert severity="success" onClose={() => setInfoMessage('')} sx={{ maxWidth: 420 }}>
                    {infoMessage}
                </Alert>
            </Snackbar>
        </Box>
    );
}

AdminUsersPanel.propTypes = {
    currentUid: PropTypes.string,
    currentEnv: PropTypes.string,
    users: PropTypes.array,
    invitations: PropTypes.array,
    organizations: PropTypes.array,
    loading: PropTypes.bool,
    error: PropTypes.string,
    onDismissError: PropTypes.func,
    onRefresh: PropTypes.func,
};

AdminUsersPanel.defaultProps = {
    users: [],
    invitations: [],
    organizations: [],
    loading: false,
    error: '',
    onDismissError: () => {},
    onRefresh: () => {},
};
